/**
 * New Joiners Hook
 *
 * Loads the manager's team members who joined within the last N days (default
 * 15), for the "New joiners" metric + drill-down modal on the Manager Dashboard.
 *
 * Source: `/data/EmployeesV2`, read per managed employee (mirrors
 * `useTeamBirthdays`'s approach) rather than one broad collection filter.
 * A `$filter` comparison across the whole EmployeesV2 collection
 * (`EmploymentStartDate ge ...`, unscoped by PersonnelNumber) reliably 400s in
 * this D365 environment — a known "SQL subquery" limitation on this composite
 * entity — and `employeeService.getEmployees` silently swallows that 400 into
 * an empty result, which is why the card always showed zero. Fetching one
 * employee at a time via `getEmployeeByNumber` (filtered by `PersonnelNumber
 * eq`) is the query shape already proven to work everywhere else in the portal
 * (`useTeamMemberDetail`, `useTeamBirthdays`), so the "since N days ago" window
 * is applied client-side instead.
 *
 * Members are mapped into the same card shape the rest of the Manager Portal
 * uses — { id, personnelNumber, name, role, status, employmentStartDate } —
 * so selecting one can open the shared TeamMemberDetailModal.
 *
 * Returns:
 *   - newJoiners: Array<{ id, personnelNumber, name, role, status, employmentStartDate }>
 *   - count: number
 *   - isLoading: boolean
 *   - error: Error | null
 */
import { useQuery } from "react-query";
import { employeeService } from "../../api/services";
import useManagerStatus from "./useManagerStatus";

const WINDOW_DAYS = 15;

/** D365 uses placeholder dates for "unset" values; treat those as no date. */
const isPlaceholderDate = (dateStr) =>
  !dateStr ||
  dateStr.startsWith("1900-01-01") ||
  new Date(dateStr).getFullYear() >= 2100;

/**
 * Inclusive lower-bound Date for the joiner window. `referenceDate` is
 * injectable for testing; defaults to now.
 */
const windowStart = (days, referenceDate = new Date()) => {
  const start = new Date(referenceDate);
  start.setDate(start.getDate() - days);
  start.setHours(0, 0, 0, 0);
  return start;
};

/**
 * Map a raw EmployeesV2 record into the card shape used across the portal.
 */
const mapJoiner = (employee, personnelNumber) => {
  const name =
    employee?.Name ||
    employee?.displayName ||
    employee?.fullName ||
    employee?.DisplayName ||
    personnelNumber;

  const role =
    employee?.PositionTitle ||
    employee?.PrimaryPositionTitle ||
    employee?.Title ||
    "—";

  const isInactive =
    employee?.IsActive === false ||
    employee?.EmploymentStatus === "Inactive" ||
    employee?.WorkerStatus === "Inactive";

  const employmentStartDate = employee?.EmploymentStartDate || null;

  return {
    id: personnelNumber,
    personnelNumber,
    name,
    role,
    status: isInactive ? "On Leave" : "Active",
    employmentStartDate: isPlaceholderDate(employmentStartDate)
      ? null
      : employmentStartDate,
  };
};

export default function useNewJoiners(days = WINDOW_DAYS) {
  const {
    managedPersonnelNumbers,
    isLoading: managerStatusLoading,
    error: managerStatusError,
  } = useManagerStatus();

  const {
    data: newJoiners,
    isLoading: joinersLoading,
    error: joinersError,
  } = useQuery(
    ["newJoiners", days, managedPersonnelNumbers],
    async () => {
      const start = windowStart(days);
      console.log(
        `🌱 Fetching new joiners since ${start.toISOString()} for ${managedPersonnelNumbers.length} managed person(s)`
      );

      // Fetch each managed employee in parallel; skip any that fail so one bad
      // record doesn't blank the whole list (mirrors useTeamBirthdays).
      const results = await Promise.all(
        managedPersonnelNumbers.map(async (personnelNumber) => {
          try {
            const res = await employeeService.getEmployeeByNumber(
              personnelNumber
            );
            const employee = res.data?.value?.[0] || null;
            return mapJoiner(employee, personnelNumber);
          } catch (err) {
            console.error(
              `❌ Failed to fetch employee for ${personnelNumber}:`,
              err
            );
            return null;
          }
        })
      );

      return results
        .filter(Boolean)
        .filter(
          (m) =>
            m.employmentStartDate &&
            new Date(m.employmentStartDate) >= start
        )
        .sort(
          (a, b) => new Date(b.employmentStartDate) - new Date(a.employmentStartDate)
        );
    },
    {
      // Only meaningful once we know who the manager manages.
      enabled: managedPersonnelNumbers.length > 0,
      retry: 2,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    newJoiners: newJoiners ?? [],
    count: newJoiners?.length ?? 0,
    isLoading:
      managerStatusLoading ||
      (managedPersonnelNumbers.length > 0 && joinersLoading),
    error: managerStatusError || joinersError || null,
  };
}
